import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

import styled from '@/styles';

const FONT = "'Manrope', sans-serif";
const GREEN = '#3E8E32';
const GREEN_PRIMARY = '#57B04F';
const GREEN_PRIMARY_HOVER = '#4CA043';

const Overlay = styled('div', {
  position: 'fixed',
  inset: 0,
  background: 'rgba(28, 28, 28, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 1000,
});

const Card = styled('div', {
  position: 'relative',
  background: '#fff',
  borderRadius: 16,
  width: '100%',
  maxWidth: 480,
  padding: '48px 40px 28px',
  boxShadow: '0 24px 56px rgba(0, 0, 0, 0.28)',
  fontFamily: FONT,
  boxSizing: 'border-box',
});

const CloseButton = styled('button', {
  position: 'absolute',
  top: 20,
  right: 20,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  display: 'flex',
  color: '#1C1C1C',
});

const IconCircle = styled('div', {
  width: 112,
  height: 112,
  borderRadius: '50%',
  background: '#EAF5E6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '4px auto 28px',
});

const Badge = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid #B8DFB0',
  background: '#F0F8ED',
  color: GREEN,
  fontSize: 14,
  fontWeight: 700,
});

const Title = styled('h2', {
  fontSize: 22,
  fontWeight: 800,
  color: '#1C1C1C',
  margin: '16px 0 12px',
});

const Description = styled('p', {
  fontSize: 15,
  lineHeight: 1.6,
  color: '#4A4A4A',
  margin: 0,
});

const Actions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 20,
  marginTop: 40,
});

const SecondaryButton = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: GREEN,
  fontWeight: 700,
  fontSize: 15,
  fontFamily: FONT,
  padding: '8px 4px',
});

const PrimaryButton = styled('button', {
  background: GREEN_PRIMARY,
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  padding: '14px 28px',
  fontWeight: 800,
  fontSize: 15,
  fontFamily: FONT,
  cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(87, 176, 79, 0.35)',
  '&:hover': { background: GREEN_PRIMARY_HOVER },
});

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    style={{ width: 22, height: 22 }}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill={GREEN} style={{ width: 16, height: 16 }}>
    <path d="M12 2.5l1.6 4.9 4.9 1.6-4.9 1.6L12 15.5l-1.6-4.9L5.5 9l4.9-1.6L12 2.5z" />
    <path d="M18.5 13.5l.7 2.1 2.1.7-2.1.7-.7 2.1-.7-2.1-2.1-.7 2.1-.7.7-2.1z" />
  </svg>
);

// Default "save a schedule" illustration: a calendar with a small save badge.
const ScheduleIcon = () => (
  <svg viewBox="0 0 56 56" fill="none" style={{ width: 56, height: 56 }}>
    <path
      d="M17 7v6M35 7v6"
      stroke="#3F9A32"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <rect
      x="9"
      y="12"
      width="34"
      height="30"
      rx="6"
      fill="#BFE3B6"
      stroke="#3F9A32"
      strokeWidth="3"
    />
    <path d="M9 21h34" stroke="#3F9A32" strokeWidth="3" />
    <rect
      x="31"
      y="29"
      width="18"
      height="18"
      rx="4"
      fill="#D7EED0"
      stroke="#3F9A32"
      strokeWidth="3"
    />
    <path
      d="M36 34v6h8"
      stroke="#3F9A32"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type WhatsNewModalProps = {
  open: boolean;
  onClose: () => void;
  onPrimary: () => void;
  title: string;
  description: string;
  badgeLabel?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  icon?: ReactNode;
};

/**
 * Presentational "What's New?" announcement modal (see design). Purely
 * controlled — visibility and persistence live in the caller. Closes on the X,
 * the secondary button, the backdrop, and Escape (all via `onClose`); the
 * primary button calls `onPrimary`.
 */
const WhatsNewModal = ({
  open,
  onClose,
  onPrimary,
  title,
  description,
  badgeLabel = "What's New?",
  primaryLabel = 'Try it now',
  secondaryLabel = 'Check later',
  icon,
}: WhatsNewModalProps) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <Card onClick={(event) => event.stopPropagation()}>
        <CloseButton type="button" aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </CloseButton>

        <IconCircle>{icon ?? <ScheduleIcon />}</IconCircle>

        <Badge>
          <SparkleIcon />
          {badgeLabel}
        </Badge>

        <Title>{title}</Title>
        <Description>{description}</Description>

        <Actions>
          <SecondaryButton type="button" onClick={onClose}>
            {secondaryLabel}
          </SecondaryButton>
          <PrimaryButton type="button" onClick={onPrimary}>
            {primaryLabel}
          </PrimaryButton>
        </Actions>
      </Card>
    </Overlay>,
    document.body,
  );
};

export default WhatsNewModal;
